// Cognito Hosted UI auth using OAuth 2.0 Authorization Code + PKCE.
// Works for both email/password (Cognito) and federated Google sign-in.
// Public SPA client (no secret) — PKCE is what protects the code exchange.
import { config } from './config';

const VERIFIER_KEY = 'pkce_verifier';
const ID_TOKEN_KEY = 'idToken';
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const EXPIRES_AT_KEY = 'expiresAt';

// ---- PKCE helpers ----
function base64UrlEncode(bytes) {
  let str = '';
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier() {
  const bytes = new Uint8Array(64);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challengeFromVerifier(verifier) {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  return base64UrlEncode(new Uint8Array(digest));
}

// ---- Login (redirect to Hosted UI) ----
// provider: undefined for the Cognito login/signup page, or 'Google'.
export async function login(provider) {
  const verifier = randomVerifier();
  const challenge = await challengeFromVerifier(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge
  });
  if (provider) params.set('identity_provider', provider);

  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

export function loginWithGoogle() {
  return login('Google');
}

// ---- Callback (exchange the authorization code for tokens) ----
export async function handleCallback() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const error = url.searchParams.get('error');
  if (error) throw new Error(url.searchParams.get('error_description') || error);
  if (!code) throw new Error('No authorization code in callback');

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier — restart sign-in');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier
  });

  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString()
  });

  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Token exchange failed: ${res.status} ${detail}`);
  }

  const tokens = await res.json();
  localStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
  localStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + tokens.expires_in * 1000));
  sessionStorage.removeItem(VERIFIER_KEY);
}

// ---- Logout ----
export function signOut() {
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
}

// ---- Token / user accessors ----
export function getIdToken() {
  const token = localStorage.getItem(ID_TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(EXPIRES_AT_KEY) || 0);
  if (!token || Date.now() >= expiresAt) return null;
  return token;
}

export function isAuthenticated() {
  return getIdToken() !== null;
}

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(payload))));
  } catch (err) {
    return null;
  }
}

// Returns the signed-in user's claims ({ sub, email, name, ... }) or null.
export function getCurrentUser() {
  const token = getIdToken();
  if (!token) return null;
  return decodeJwt(token);
}
